import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { CSVLink } from "react-csv";
import { useTranslation } from "react-i18next";
import { useHistory } from "react-router-dom";
import { Kpi } from "../../services/analytic/kpi";
import languageService from "../../services/core/language-service";
import StringHelpers from "../../services/core/string-helpers";
import Microsite from "../../services/micro-site/microsite";

type Props = {
  model: Kpi;
  microsites: Array<Microsite>;
  isLocketgoAdmin: boolean;
  exportFileName: string;
  title: string;
};

type EventData = {
  Name: string;
  Organization: string;
  Reservations: number;
  Confirmed: number;
  TotalAmount: string;
  StartOn: string;
};

export default function EventStatsTable(props: Props) {
  const { model, isLocketgoAdmin, microsites, exportFileName } = props;
  const { t } = useTranslation();
  const history = useHistory();

  const datas: Array<EventData> = [];
  model.values.forEach((elem) => {
    const microsite = microsites.find((m) => m.reservationBlocks.some((rb) => rb.id === elem.entityId));
    const reservationBlock = microsite?.reservationBlocks.find((rb) => rb.id === elem.entityId);

    datas.push({
      Name: elem.name,
      Organization: elem.organizationName,
      Reservations: elem.count,
      Confirmed: elem.countPaid,
      TotalAmount: !elem.currency
        ? "N/A"
        : t("common.price-format", { price: StringHelpers.formatPrice(elem.amountGross), currency: elem.currency }),
      StartOn: !reservationBlock?.startOn
        ? "N/A"
        : StringHelpers.formatDateTime(new Date(reservationBlock.startOn), "America/Toronto") || "N/A",
    });
  });

  model.values.sort((a, b) => {
    const dataA = datas.find((d) => d.Name === a.name);
    const dataB = datas.find((d) => d.Name === b.name);

    if (dataA && dataB) {
      const startOnA = new Date(dataA.StartOn);
      const startOnB = new Date(dataB.StartOn);

      if (startOnA.valueOf() && startOnB.valueOf()) {
        const dif = startOnB.valueOf() - startOnA.valueOf();

        if (dif > 0) return 1;
        if (dif < 0) return -1;
        return 0;
      }

      if (!startOnA.valueOf() && startOnB.valueOf()) {
        return 1;
      }

      if (startOnA.valueOf() && !startOnB.valueOf()) {
        return -1;
      }

      return 0;
    }

    const dif = b.count - a.count + (b.countPaid - a.countPaid);
    if (dif > 0) return 1;
    if (dif < 0) return -1;
    return 0;
  });

  return (
    <div className="col-12">
      <table className="table table-sm mt-4 table-hover row-clikable">
        <thead>
          <tr>
            <th>
              {t("analytic.events")}
              <CSVLink
                className="btn btn-outline-primary ms-2"
                title={t("common.export")}
                data={datas}
                enclosingCharacter={`"`}
                filename={exportFileName}
              >
                <FontAwesomeIcon icon="file-export"></FontAwesomeIcon>
              </CSVLink>
            </th>
            <th className="text-end">{t("analytic.organization")}</th>
            <th className="text-end">{t("analytic.start-on")}</th>
            <th className="text-end">{t("analytic.reserved")}</th>
            {isLocketgoAdmin && <th className="text-end">{t("analytic.confirmed")}</th>}
            <th className="text-end">{t("analytic.total-amount")}</th>
            <th></th>
          </tr>
        </thead>
        <tbody>
          {model.values.map((kpi) => {
            const microsite = microsites.find((m) => m.reservationBlocks.some((rb) => rb.id === kpi.entityId));
            const reservationBlock = microsite?.reservationBlocks.find((rb) => rb.id === kpi.entityId);
            const data = datas.find((d) => d.Name === kpi.name);
            const url =
              microsite && reservationBlock
                ? `/organizations/${microsite?.organizationId}/microsites/${microsite?.id}/reservationBlocks/${reservationBlock?.id}/sales-report`
                : "/";

            if (!reservationBlock) {
              return <></>;
            }

            return (
              <tr key={kpi.name} onClick={() => history.push(url)}>
                <td className="pt-2 pb-2">{kpi.translatedName[languageService.language] ?? kpi.name}</td>
                <td className="text-end pt-2 pb-2">{kpi.organizationName}</td>
                <td className="text-end pt-2 pb-2">{data?.StartOn}</td>
                <td className="text-end pt-2 pb-2">{kpi.count}</td>
                {isLocketgoAdmin && <td className="text-end pt-2 pb-2">{kpi.countPaid}</td>}
                <td className="text-end pt-2 pb-2">
                  {!kpi.currency
                    ? "N/A"
                    : t("common.price-format", {
                        price: StringHelpers.formatPrice(kpi.amountGross),
                        currency: kpi.currency,
                      })}
                </td>
                <td className="ps-4 pt-2 pb-2">
                  <FontAwesomeIcon icon={["fas", "angle-right"]} />
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}
